import { useState } from "react";
import { Trans } from "@lingui/react/macro";
import type { FullResourceFragment } from "~/graphql/codegen/graphql";
import { classNames } from "~/utils";

/**
 * "Key shares from the coaches" — coach prompts/links + coach-answered Q&A
 * extracted from the live-session Zoom chat (see server article.coach_highlights).
 * Rendered at the top of the transcript column on the recording's annotate page.
 * Hidden entirely when there's nothing to show.
 */
export function AnnotateCoachHighlights({
  article,
}: {
  article: FullResourceFragment;
}) {
  const highlights = article.coachHighlights;
  const shares = highlights?.shares ?? [];
  const qa = highlights?.questionsAnswered ?? [];

  if (highlights?.state !== "ready" || (shares.length === 0 && qa.length === 0)) {
    return null;
  }

  return (
    <section className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
      <h3 className="text-base font-semibold text-foreground">
        <Trans>Key shares from the coaches</Trans>
      </h3>

      {shares.length > 0 && (
        <ul className="space-y-3">
          {shares.map((share, i) => (
            <li key={`share-${i}`} className="text-sm">
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {share.coach}
                </span>
                {share.kind !== "link" && <CopyButton text={share.text} />}
              </div>
              {share.kind === "link" && share.url ? (
                <a
                  href={share.url}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-indigo-600 hover:text-indigo-800 underline"
                >
                  {share.text || share.url}
                </a>
              ) : (
                <p className="whitespace-pre-wrap text-foreground">{share.text}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {qa.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            <Trans>Questions answered</Trans>
          </h4>
          {qa.map((item, i) => (
            <div key={`qa-${i}`} className="text-sm">
              <p className="font-medium text-foreground">{item.question}</p>
              <div className="mt-1 flex items-start justify-between gap-2">
                <p className="whitespace-pre-wrap text-muted-foreground">
                  <span className="font-medium">{item.coach}: </span>
                  {item.answer}
                </p>
                <CopyButton text={item.answer} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable — no-op */
        }
      }}
      className={classNames(
        "shrink-0 rounded px-2 py-0.5 text-xs",
        copied
          ? "text-green-700"
          : "text-indigo-600 hover:bg-muted hover:text-indigo-800",
      )}
    >
      {copied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
    </button>
  );
}
